// src/modules/aiml/AIMLModule.jsx
// AI/ML JS Libraries Module — Shell + Navigation
// Real pretrained models running in-browser via WASM/WebGPU. No server. No API keys.

import React, { useState, useEffect, useCallback } from 'react';
import TransformersSection  from './TransformersSection.jsx';
import BrainSection         from './BrainSection.jsx';
import TensorFlowSection    from './TensorFlowSection.jsx';

// Lazy stubs for sections not yet implemented
const ComingSoon = ({ name, accent, icon, features }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px' }}>
    <div style={{ padding: '32px', background: 'var(--raised)', border: `1px solid ${accent}33`, borderRadius: '6px', textAlign: 'center' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>{icon}</div>
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '20px', color: accent, marginBottom: '8px' }}>{name}</div>
      <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '20px' }}>Implementation coming next — architecture fully designed</div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {features.map(f => (
          <span key={f} style={{ padding: '4px 12px', background: accent + '18', border: `1px solid ${accent}44`, color: accent, fontSize: '10px', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>{f}</span>
        ))}
      </div>
    </div>
  </div>
);

const SECTIONS = [
  {
    id: 'transformers',
    label: 'Transformers.js',
    icon: '⚡',
    accent: '#f59e0b',
    sub: 'BERT · Whisper · CLIP · LLaMA — 1200+ models',
    tier: 1,
    status: 'live',
    component: TransformersSection,
    features: [],
  },
  {
    id: 'webllm',
    label: 'WebLLM',
    icon: '⟁',
    accent: '#34d399',
    sub: 'Llama-3 · Phi-3.5 · Gemma-2 · Mistral in-browser',
    tier: 1,
    status: 'planned',
    features: ['Phi-3.5-mini 2.7GB','Gemma-2-2B 1.3GB','SmolLM2 900MB','Streaming chat','Benchmark','OpenAI-compat API'],
  },
  {
    id: 'tensorflow',
    label: 'TensorFlow.js',
    icon: '◈',
    accent: '#f97316',
    sub: 'Custom train · MobileNet · COCO-SSD · PoseNet',
    tier: 2,
    status: 'live',
    component: TensorFlowSection,
    features: [],
  },
  {
    id: 'mediapipe',
    label: 'MediaPipe',
    icon: '◎',
    accent: '#06b6d4',
    sub: 'Face 478pts · Hands 21pts · Pose · Gesture',
    tier: 2,
    status: 'planned',
    features: ['Face 478 landmarks','Hands 21 pts/hand','Pose 33 keypoints','Gesture recognition','Object detection','Selfie segmentation'],
  },
  {
    id: 'brainjs',
    label: 'Brain.js',
    icon: '⬡',
    accent: '#ef4444',
    sub: 'Build · Train · Visualize NNs in browser',
    tier: 2,
    status: 'live',
    component: BrainSection,
    features: [],
  },
  {
    id: 'onnx',
    label: 'ONNX Runtime',
    icon: '◉',
    accent: '#a78bfa',
    sub: 'Run any .onnx model — PyTorch / TF export',
    tier: 2,
    status: 'planned',
    features: ['Upload .onnx','Tensor inspection','Run inference','Latency benchmark','WebGPU provider','Op support table'],
  },
  {
    id: 'faceapi',
    label: 'face-api.js',
    icon: '◐',
    accent: '#f472b6',
    sub: 'Detect · Landmarks · Emotion · Age/Gender',
    tier: 2,
    status: 'planned',
    features: ['SSD detection','68 landmarks','7 emotions','Age ± MAE','Gender prob','FaceNet 128D match'],
  },
  {
    id: 'ml5',
    label: 'ml5.js',
    icon: '◫',
    accent: '#10b981',
    sub: 'Creative ML · Image · Sound · Pose · Neural',
    tier: 2,
    status: 'planned',
    features: ['Image classify','Sound commands','Body pose','Hand pose','Sentiment','Custom neural net'],
  },
];

// ── WebGPU Detection ──────────────────────────────────────────────
async function detectWebGPU() {
  if (typeof navigator === 'undefined' || !navigator.gpu) {
    return { supported: false, reason: 'WebGPU API not available in this browser' };
  }
  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) return { supported: false, reason: 'No compatible GPU adapter found' };
    return { supported: true, adapter };
  } catch (e) {
    return { supported: false, reason: e.message };
  }
}

// ── Shared components (exported for use in sections) ──────────────
export const ModelLoader = ({ status, progress, modelName, modelSize, accent = '#f59e0b', onLoad, children }) => {
  const col = {
    idle:        accent,
    detecting:   'var(--cyan)',
    downloading: 'var(--gold)',
    loading:     accent,
    ready:       'var(--green)',
    error:       'var(--red)',
  }[status] || accent;

  return (
    <div style={{ padding: '20px 24px', background: 'var(--raised)', border: `1px solid ${col}33`, borderRadius: '6px' }}>
      {status === 'idle' && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '14px', color: accent, marginBottom: '4px' }}>{modelName}</div>
            <div style={{ fontSize: '11px', color: 'var(--t3)' }}>Size: {modelSize} · Cached after first load · Runs in your browser</div>
          </div>
          <button onClick={onLoad} style={{ padding: '10px 20px', background: accent + '20', border: `1px solid ${accent}`, color: accent, fontSize: '11px', cursor: 'pointer', borderRadius: '4px', fontFamily: 'JetBrains Mono', fontWeight: 700, whiteSpace: 'nowrap' }}>
            LOAD MODEL →
          </button>
        </div>
      )}
      {(status === 'detecting' || status === 'downloading' || status === 'loading') && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', color: col, fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
              {status === 'detecting' ? 'DETECTING BACKEND…' : status === 'downloading' ? `DOWNLOADING ${modelName.toUpperCase()}…` : 'INITIALIZING MODEL…'}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--t3)', fontFamily: 'JetBrains Mono' }}>{progress}%</span>
          </div>
          <div style={{ height: '6px', background: 'var(--deep)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: col, borderRadius: '3px', transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '6px' }}>
            Models are cached in your browser — this download only happens once
          </div>
        </div>
      )}
      {status === 'ready' && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ width: '8px', height: '8px', background: 'var(--green)', borderRadius: '50%', flexShrink: 0 }} />
          <span style={{ fontSize: '12px', color: 'var(--green)', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{modelName} — READY</span>
          <span style={{ fontSize: '10px', color: 'var(--t3)' }}>· {modelSize} · cached</span>
          {children}
        </div>
      )}
      {status === 'error' && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '12px', color: 'var(--red)', fontFamily: 'JetBrains Mono', flex: 1 }}>
            ✗ Failed to load {modelName}
          </div>
          <button onClick={onLoad} style={{ padding: '6px 14px', background: 'var(--red)18', border: '1px solid var(--red)', color: 'var(--red)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono' }}>
            RETRY
          </button>
        </div>
      )}
    </div>
  );
};

export const WebGPUBadge = ({ gpuInfo }) => {
  if (!gpuInfo) return null;
  return (
    <span style={{
      padding: '3px 10px',
      background: gpuInfo.supported ? 'var(--green)18' : 'var(--orange)18',
      border: `1px solid ${gpuInfo.supported ? 'var(--green)' : 'var(--orange)'}44`,
      color: gpuInfo.supported ? 'var(--green)' : 'var(--orange)',
      fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700,
    }}>
      {gpuInfo.supported ? '⚡ WebGPU' : '○ WASM only'}
    </span>
  );
};

export const InferenceTag = ({ ms, color = 'var(--green)' }) => (
  <span style={{ padding: '2px 8px', background: color + '18', border: `1px solid ${color}44`, color, fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
    {ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`}
  </span>
);

// ── Main Module ───────────────────────────────────────────────────
export default function AIMLModule() {
  const [activeSection, setActiveSection] = useState('transformers');
  const [gpuInfo, setGpuInfo] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    detectWebGPU().then(setGpuInfo);
  }, []);

  const active = SECTIONS.find(s => s.id === activeSection);

  return (
    <div style={{ display: 'flex', gap: '0', height: '100%', minHeight: '600px' }}>
      {/* ── Sidebar ── */}
      <div style={{
        width: sidebarCollapsed ? '48px' : '220px',
        flexShrink: 0,
        background: 'var(--surface)',
        borderRight: '1px solid var(--b0)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }}>
        {/* Sidebar header */}
        <div style={{ padding: '14px 12px', borderBottom: '1px solid var(--b0)', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          {!sidebarCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '11px', color: '#f59e0b', letterSpacing: '.08em' }}>AI/ML LIBS</div>
              <div style={{ fontSize: '9px', color: 'var(--t3)', marginTop: '1px' }}>8 libraries · in-browser</div>
            </div>
          )}
          <button onClick={() => setSidebarCollapsed(c => !c)} style={{ background: 'transparent', border: '1px solid var(--b0)', color: 'var(--t3)', width: '22px', height: '22px', cursor: 'pointer', borderRadius: '2px', fontSize: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {sidebarCollapsed ? '›' : '‹'}
          </button>
        </div>

        {/* WebGPU status */}
        {!sidebarCollapsed && gpuInfo && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--b0)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: gpuInfo.supported ? 'var(--green)' : 'var(--orange)', flexShrink: 0 }} />
              <span style={{ fontSize: '9px', color: gpuInfo.supported ? 'var(--green)' : 'var(--orange)', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                {gpuInfo.supported ? 'WebGPU ready' : 'WASM fallback'}
              </span>
            </div>
          </div>
        )}

        {/* Tier 1 */}
        {!sidebarCollapsed && (
          <div style={{ padding: '8px 12px 4px', fontSize: '8px', color: 'var(--t3)', letterSpacing: '.12em', flexShrink: 0 }}>TIER 1 — FLAGSHIP</div>
        )}
        {SECTIONS.filter(s => s.tier === 1).map(sec => (
          <SidebarItem key={sec.id} sec={sec} active={activeSection === sec.id} collapsed={sidebarCollapsed} onClick={() => setActiveSection(sec.id)} />
        ))}

        {/* Tier 2 */}
        {!sidebarCollapsed && (
          <div style={{ padding: '10px 12px 4px', fontSize: '8px', color: 'var(--t3)', letterSpacing: '.12em', flexShrink: 0 }}>TIER 2 — SPECIALIST</div>
        )}
        {SECTIONS.filter(s => s.tier === 2).map(sec => (
          <SidebarItem key={sec.id} sec={sec} active={activeSection === sec.id} collapsed={sidebarCollapsed} onClick={() => setActiveSection(sec.id)} />
        ))}

        {/* Footer */}
        {!sidebarCollapsed && (
          <div style={{ marginTop: 'auto', padding: '10px 12px', borderTop: '1px solid var(--b0)', flexShrink: 0 }}>
            <div style={{ fontSize: '9px', color: 'var(--t3)', lineHeight: 1.6 }}>
              100% client-side · No server · No API keys · Private by design
            </div>
          </div>
        )}
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px', minWidth: 0 }}>
        {/* Section header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', background: 'var(--surface)',
          border: '1px solid var(--b0)', borderRadius: '4px',
          borderLeft: `3px solid ${active?.accent || '#f59e0b'}`,
          marginBottom: '14px',
        }}>
          <span style={{ fontSize: '22px' }}>{active?.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '15px', color: active?.accent }}>{active?.label?.toUpperCase()}</div>
            <div style={{ fontSize: '11px', color: 'var(--t3)' }}>{active?.sub}</div>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
            {active?.status === 'live' && (
              <span style={{ padding: '3px 10px', background: 'var(--green)18', border: '1px solid var(--green)44', color: 'var(--green)', fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>✓ LIVE</span>
            )}
            {active?.status === 'planned' && (
              <span style={{ padding: '3px 10px', background: 'var(--orange)18', border: '1px solid var(--orange)44', color: 'var(--orange)', fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>NEXT</span>
            )}
            <WebGPUBadge gpuInfo={gpuInfo} />
          </div>
        </div>

        {/* Section content */}
        {active?.status === 'live' && active?.component
          ? <active.component gpuInfo={gpuInfo} />
          : active?.status === 'planned'
          ? <ComingSoon name={active.label} accent={active.accent} icon={active.icon} features={active.features} />
          : null
        }
      </div>
    </div>
  );
}

// ── Sidebar Item ──────────────────────────────────────────────────
function SidebarItem({ sec, active, collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? `${sec.label} — ${sec.sub}` : ''}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: collapsed ? '10px 0' : '8px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        background: active ? sec.accent + '15' : 'transparent',
        border: 'none',
        borderLeft: active ? `2px solid ${sec.accent}` : '2px solid transparent',
        color: active ? sec.accent : 'var(--t2)',
        cursor: 'pointer', width: '100%', textAlign: 'left',
        transition: 'all 0.15s',
        flexShrink: 0,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = sec.accent + '08'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ fontSize: '15px', flexShrink: 0 }}>{sec.icon}</span>
      {!collapsed && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '11px', fontWeight: active ? 700 : 400, fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sec.label}</div>
          {sec.status === 'planned' && (
            <div style={{ fontSize: '8px', color: 'var(--t3)', marginTop: '1px' }}>planned</div>
          )}
        </div>
      )}
    </button>
  );
}
